/**
 * Eine kurze Texteingabe als eigenes Blatt statt window.prompt().
 *
 * Warum nicht prompt()?
 *   Als Home-Bildschirm-App auf dem iPad ist das native Fenster unzuverlässig:
 *   nach einem Abbrechen kamen Tipper auf die Anlegen-Knöpfe nicht mehr an.
 *   Ein eigenes Blatt gehört der App, sieht aus wie die übrigen Dialoge
 *   (dieselben modal-Klassen wie "Neues Projekt") und lässt sich mit dem
 *   Pencil per Scribble beschreiben.
 *
 * Ergebnis: der eingegebene Text (getrimmt), oder null bei Abbrechen.
 * Ein leeres Feld gilt als Abbrechen, ausser bei allowEmpty – dann kommt ''.
 */
import { button, field, h, textInput } from './dom.ts';

export function askText(opts: {
  title: string;
  label: string;
  placeholder?: string;
  value?: string;
  confirm?: string;
  allowEmpty?: boolean;
  /** z. B. 'numeric' fuer die Zahlentastatur auf dem iPad. */
  inputMode?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const input = textInput({ value: opts.value, placeholder: opts.placeholder, inputMode: opts.inputMode });

    const modal = h(
      'div',
      { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true' } },
      h(
        'div',
        { class: 'modal__panel' },
        h(
          'div',
          { class: 'modal__head' },
          h('h2', { class: 'modal__title', text: opts.title }),
          button('Abbrechen', () => done(null), { class: 'btn btn--small' }),
        ),
        h('div', { class: 'modal__body' }, field(opts.label, input)),
        h(
          'div',
          { class: 'modal__foot' },
          button(opts.confirm ?? 'Anlegen', () => done(input.value), { class: 'btn btn--primary' }),
        ),
      ),
    );

    let finished = false;
    function done(value: string | null): void {
      if (finished) return;
      finished = true;
      modal.remove();
      if (value === null) return resolve(null);
      const text = value.trim();
      resolve(text === '' && !opts.allowEmpty ? null : text);
    }

    // Tippen auf den Hintergrund bricht ab – wie in den übrigen Dialogen.
    modal.addEventListener('click', (ev) => {
      if (ev.target === modal) done(null);
    });
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') done(input.value);
      else if (ev.key === 'Escape') done(null);
    });

    document.body.append(modal);
    input.focus();
  });
}

/**
 * "Bist du dir sicher?" als eigenes Blatt statt window.confirm() – aus
 * demselben Grund wie askText: native Fenster sind auf dem iPad unzuverlaessig.
 * Ergebnis: true bei Bestaetigen, false bei Abbrechen oder Tippen daneben.
 */
export function askConfirm(opts: { title: string; text: string; confirm: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = h(
      'div',
      { class: 'modal', attrs: { role: 'alertdialog', 'aria-modal': 'true' } },
      h(
        'div',
        { class: 'modal__panel' },
        h('div', { class: 'modal__head' }, h('h2', { class: 'modal__title', text: opts.title })),
        h('div', { class: 'modal__body' }, h('p', { text: opts.text })),
        h(
          'div',
          { class: 'modal__foot btn-row' },
          button('Abbrechen', () => done(false), { class: 'btn' }),
          button(opts.confirm, () => done(true), { class: opts.danger ? 'btn btn--danger' : 'btn btn--primary' }),
        ),
      ),
    );

    let finished = false;
    function done(value: boolean): void {
      if (finished) return;
      finished = true;
      modal.remove();
      document.removeEventListener('keydown', onKey);
      resolve(value);
    }
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') done(false);
    };
    modal.addEventListener('click', (ev) => {
      if (ev.target === modal) done(false);
    });
    document.addEventListener('keydown', onKey);
    document.body.append(modal);
  });
}
